import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { isSupabaseConfigured, supabase, supabaseConfigError } from '../services/supabaseClient';

const AuthContext = createContext(null);
const AUTH_TIMEOUT_MS = 8000;
const PROFILE_TIMEOUT_MS = 8000;

function withTimeout(promise, timeoutMs, message) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) window.clearTimeout(timeoutId);
  });
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  async function loadProfile(userId) {
    if (!isSupabaseConfigured || !userId) {
      setProfile(null);
      return null;
    }

    try {
      const { data, error } = await withTimeout(
        supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
        PROFILE_TIMEOUT_MS,
        'Profile lookup timed out. Check Supabase table policies and network access.'
      );

      if (error) throw error;
      setProfile(data);
      setAuthError('');
      return data;
    } catch (error) {
      console.error('Unable to load user profile:', error);
      setProfile(null);
      setAuthError(error?.message || 'Unable to load user profile.');
      return null;
    }
  }

  useEffect(() => {
    let mounted = true;

    async function init() {
      try {
        setLoading(true);
        setAuthError('');

        if (!isSupabaseConfigured) {
          setSession(null);
          setUser(null);
          setProfile(null);
          setAuthError(supabaseConfigError || 'Supabase is not configured.');
          return;
        }

        const { data: sessionData, error } = await withTimeout(
          supabase.auth.getSession(),
          AUTH_TIMEOUT_MS,
          'Supabase auth check timed out. Verify REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY and Supabase Auth settings.'
        );

        if (!mounted) return;
        if (error) throw error;

        const nextSession = sessionData?.session || null;
        const nextUser = nextSession?.user || null;

        setSession(nextSession);
        setUser(nextUser);

        // Do not block route rendering while the profile loads. A slow profile query was
        // causing the production site to stay on “Checking access...” indefinitely.
        if (nextUser) {
          loadProfile(nextUser.id);
        } else {
          setProfile(null);
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        if (!mounted) return;
        setSession(null);
        setUser(null);
        setProfile(null);
        setAuthError(error?.message || 'Unable to initialize authentication.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    init();

    let subscription;
    if (isSupabaseConfigured) {
      const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
        const nextUser = nextSession?.user || null;
        setSession(nextSession);
        setUser(nextUser);
        setLoading(false);

        if (nextUser) {
          // Keep the auth callback synchronous; profile fetch runs separately.
          window.setTimeout(() => loadProfile(nextUser.id), 0);
        } else {
          setProfile(null);
        }
      });
      subscription = data.subscription;
    }

    return () => {
      mounted = false;
      if (subscription) subscription.unsubscribe();
    };
  }, []);

  async function signIn(email, password) {
    if (!isSupabaseConfigured) throw new Error(supabaseConfigError);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }

  async function signUp(email, password, fullName) {
    if (!isSupabaseConfigured) throw new Error(supabaseConfigError);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    });
    if (error) throw error;
  }

  async function signOut() {
    if (!isSupabaseConfigured) throw new Error(supabaseConfigError);
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }

  const value = useMemo(() => ({
    session,
    user,
    profile,
    loading,
    authError,
    isAdmin: profile?.role === 'admin',
    isSupabaseConfigured,
    supabaseConfigError,
    signIn,
    signUp,
    signOut,
    reloadProfile: () => loadProfile(user?.id),
  }), [session, user, profile, loading, authError]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
